import '@schmancy/primitives/icons'
import '@schmancy/primitives/option/option'
import '@schmancy/primitives/surface/surface'
import '@schmancy/primitives/button/icon-button'
import '@schmancy/primitives/tone-dot/tone-dot'
import '@schmancy/primitives/voice-note'
import { fromResizeObserver } from '@schmancy/directives/layout'
import { componentText, interpolate, SchmancyFormField } from '@schmancy/mixins'
import { css, html, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { repeat } from 'lit/directives/repeat.js'
import {
	animationFrames,
	animationFrameScheduler,
	BehaviorSubject,
	catchError,
	distinctUntilChanged,
	EMPTY,
	defer,
	fromEvent,
	ignoreElements,
	exhaustMap,
	filter,
	finalize,
	from,
	interval,
	map,
	merge,
	mergeMap,
	Observable,
	observeOn,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
	timer,
} from 'rxjs'
import { acceptRefusal, acceptsFile, type FileRefusal } from '../dropzone/accept'
import './file-chips'

export type SchmancyMessageSendEvent = CustomEvent<{ value: string }>

/** A finished voice note. Sending or releasing a held microphone sets `send`;
 *  ending it while busy or adding files keeps it with the draft instead. */
export interface VoiceNoteDetail {
	audio: Blob
	seconds: number
	peaks: number[]
	send: boolean
}
/** A consumer that cannot keep what it was handed says why, in a plain sentence
 *  the composer shows above the input until the next file or note arrives. */
export interface ComposerRefuse {
	refuse(message: string): void
}
export type SchmancyVoiceNoteEvent = CustomEvent<VoiceNoteDetail & ComposerRefuse>

/** Files attached by the button, a drop or a paste, past `accept` and the cap.
 *  `until` holds Send and shows "Adding files…" while the consumer prepares
 *  them; a rejection is shown as a refusal. */
export type SchmancyComposerFileEvent = CustomEvent<
	{ files: File[]; until(work: Promise<unknown>): void } & ComposerRefuse
>

/** One thing the `/` overlay offers. The words are the host's; `value` says which it is. */
export interface ComposerCommand {
	value: string
	label: string
	/** A second, smaller line under the label. */
	secondary?: string
	/** A Material Symbols name, drawn before the label. */
	icon?: string
}

/** What the person chose from the `/` overlay. Cancel it to keep the overlay open and hand it other `commands`. */
export type SchmancyComposerCommandEvent = CustomEvent<{ command: ComposerCommand }>

/** The note a draft holds, drawn in the pill and offering its removal. */
export interface ComposerVoiceNote {
	src: string
	seconds: number
	peaks: number[]
	transcript?: string
}

/** What the overlay says of how many it offers, each in the host's words; `other` may say `{{count}}`. */
export interface ComposerCommandCounts {
	none: string
	one: string
	other: string
}

/** The gap between the pill and the `/` overlay, and how near the window's edge the overlay may reach. */
const COMMANDS_GAP_PX = 8
const COMMANDS_EDGE_MARGIN_PX = 16
/** Below this much room above the composer, the overlay opens under it, if there is more there. */
const COMMANDS_ROOM_PX = 160

/** Bars in the live waveform and in a finished note's peaks. */
const VOICE_BARS = 40
/** A held finger slid this far left throws the recording away. */
const CANCEL_SLIDE_PX = 80
/** A held finger slid this far up locks the recording hands-free. */
const LOCK_SLIDE_PX = 60
/** The long-press directive's hold threshold also distinguishes a pending touch tap. */
const TOUCH_HOLD_MS = 500

const clock = (tenths: number): string => {
	const whole = Math.floor(tenths / 10)
	return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`
}

/**
 * A single multiline message field. Consumers own admission and busy state.
 *
 * @slot leading - Tools at the start of a row under the field, inside the pill:
 *   attach, point, capture.
 * @slot trailing - Tools at the end of that row. The row renders only while
 *   either slot holds something, `attach` puts the paperclip at its start, or
 *   `voice` puts the microphone at its end.
 * @slot attachments - What the draft holds besides its words, inside the pill
 *   above the text: `<schmancy-file-chips>`, a `removable` `<schmancy-voice-note>`.
 *   Anything slotted here is draft content, so Send stands without words;
 *   render it only while the draft holds something.
 * @slot note - A line above the input saying why the draft waits or what went
 *   wrong: the host's words, taking no space while empty. Not draft content.
 *
 * @fires file - `{ files, refuse, until }` — files the paperclip, a drop or a
 *   paste attached, already past `accept` and the cap.
 * @fires refusal - `{ file, cause, message }` — one per file turned away by
 *   `accept` or the cap, the same event `<schmancy-dropzone>` fires.
 * @fires voice-note - A recording ended and was kept: `{ audio, seconds, peaks,
 *   send, refuse }`. Discarded recordings fire nothing.
 * @fires voice-note-remove - The remove control of the held `voiceNote` was pressed.
 * @fires remove - `{ file, index }` — a chip of the held `files` asked to leave.
 * @fires preview - `{ file, index, files }` — a chip's name was pressed; cancelable.
 * @fires command - `{ command }` — one of `commands` was chosen; the field is
 *   empty by then. Cancelable: canceling keeps the overlay open for other `commands`.
 * @fires commands-close - The overlay closed, by a choice or without one. Hand back the first `commands` here.
 */
@customElement('schmancy-message-composer')
export class SchmancyMessageComposer extends SchmancyFormField() {
	static styles = [
		css`
			:host {
				display: block;
				min-width: 0;
				/* What a container it stands in sets, for corners concentric with its own. */
				border-radius: var(--schmancy-message-composer-radius, var(--schmancy-radius-3xl, 1.75rem));
			}
		`,
	]

	@property({ type: String }) override value = ''
	#placeholder: string | null = null
	@property()
	get placeholder(): string {
		return this.#placeholder ?? componentText('messageComposer.message')
	}
	set placeholder(value: string) {
		this.#placeholder = value
	}
	@property({ type: String }) size: 'sm' | 'md' = 'md'
	@property({ type: Boolean }) busy = false
	@property({ type: Boolean }) hideSend = false
	/** The consumer holds sendable draft content outside the attachment slot. */
	@property({ type: Boolean }) hasContent = false
	@property({ type: Number }) maxRows = 6
	@property({ type: Boolean }) override autofocus = false
	/**
	 * Record, pause, listen and resume inline. Send ends the recording directly;
	 * Enter sends and Escape discards. Holding on touch sends on release, sliding
	 * left discards and sliding up locks hands-free. While busy, an ended note
	 * stays in the draft.
	 */
	@property({ type: Boolean }) voice = false

	/**
	 * Take files: a paperclip at the start of the tool row opens the picker, and
	 * files dropped on the pill or pasted into it arrive the same way. Each
	 * batch fires `file`; the consumer holds the files.
	 */
	@property({ type: Boolean }) attach = false

	/** The platform's `accept` grammar, applied to picked, dropped and pasted files. Empty takes any file. */
	@property({ type: String }) accept = ''

	/** How many files a message holds. */
	@property({ type: Number, attribute: 'max-files' }) maxFiles = Number.POSITIVE_INFINITY

	/** How many files the draft holds now, counted against `maxFiles`. */
	@property({ type: Number, attribute: 'held-files' }) heldFiles = 0

	/** The files the draft holds, drawn as removable chips in the pill and counted against `maxFiles`. */
	@property({ attribute: false }) files: readonly File[] = []

	/** The voice note the draft holds, drawn in the pill. */
	@property({ attribute: false }) voiceNote: ComposerVoiceNote | null = null

	/**
	 * What `/` at the very start of an empty field offers, in an overlay above the
	 * composer that takes no room from it. The person filters by typing and moves
	 * with the arrows; Enter chooses and Escape closes, from the field. Replaced
	 * while open, the overlay shows the new items. Empty, `/` is an ordinary character.
	 */
	@property({ attribute: false }) commands: readonly ComposerCommand[] = []

	/** The overlay's accessible name. Without one it answers to the field's. */
	@property({ attribute: 'commands-label' }) commandsLabel = ''

	/** What is announced of how many the overlay offers. Without them nothing is. */
	@property({ attribute: false }) commandCounts: ComposerCommandCounts | null = null

	/** A node is assigned to `leading` or `trailing`, so the tool row shows. */
	@state() private hasTools = false
	/** `off` until the microphone is live; `hold` while a finger keeps it down. */
	@state() private recording: 'off' | 'tap' | 'hold' | 'locked' | 'paused' = 'off'
	@state() private preview: { src: string; seconds: number; peaks: number[] } | null = null
	@state() private tenths = 0
	/** The newest levels, 0 to 1, oldest first. */
	@state() private levels: number[] = []
	@state() private micRefused = false
	/** Something is slotted into `attachments`, so the draft can be sent without words. */
	@state() private hasAttachments = false
	/** Something is slotted into `note`. */
	@state() private hasNote = false
	/** What precedes the overlay's filter in the field while it is open: the `/` that opened it, or nothing once a choice kept it open. */
	@state() private commandPrefix: '/' | '' | null = null
	@state() private activeCommand = 0
	/** Files are dragged over the pill. */
	@state() private dragging = false
	/** Batches the consumer is still preparing through `until`. */
	@state() private adding = 0
	/** The refusals of the last file batch or voice note, each said once. */
	@state() private notices: string[] = []
	private dragDepth = 0
	private readonly picked$ = new Subject<File[]>()
	private readonly picker = createRef<HTMLInputElement>()
	private readonly record$ = new Subject<'tap' | 'hold'>()
	private readonly stop$ = new Subject<'keep' | 'send' | 'discard'>()
	private readonly pause$ = new Subject<void>()
	private readonly cancelVoice$ = new Subject<'keep' | 'discard'>()
	private voiceDone: Promise<void> | null = null
	private holdStart: { x: number; y: number; at: number } | null = null
	private textarea = createRef<HTMLTextAreaElement>()
	private readonly recordingRow = createRef<HTMLDivElement>()
	private readonly pill = createRef<HTMLDivElement>()
	private readonly commandsLayer = createRef<HTMLElement>()
	private readonly commandsShown$ = new BehaviorSubject(false)

	override connectedCallback(): void {
		super.connectedCallback()
		const refuse = (message: string): void => {
			if (!this.notices.includes(message)) this.notices = [...this.notices, message]
		}
		merge(
			// Text reflows only when its width moves. The height it then takes moves this element too, so it is set a
			// frame later, outside the observer's own delivery, or the browser reports a loop.
			fromResizeObserver(this).pipe(
				map(([entry]) => entry?.contentRect.width),
				distinctUntilChanged(),
				observeOn(animationFrameScheduler),
				tap(() => this.resize()),
			),
			// A hybrid device changes pointer mid-session, and the tool row's target follows it.
			fromEvent(window.matchMedia('(pointer: coarse)'), 'change').pipe(tap(() => this.requestUpdate())),
			// The pill moves for reasons no event announces while the overlay stands above it.
			this.commandsShown$.pipe(
				distinctUntilChanged(),
				switchMap(shown => (shown ? animationFrames() : EMPTY)),
				tap(() => this.placeCommands()),
			),
			// One recording at a time: a second press while the microphone opens or
			// records is dropped, and the session ends on the first stop.
			this.record$.pipe(
				exhaustMap(mode =>
					defer(() => {
						let resolve: () => void = () => {}
						this.voiceDone = new Promise<void>(done => { resolve = done })
						return new Observable<MediaStream>(subscriber => {
							this.notices = []
							navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
								// Permission can finish after disconnection cancelled the request's owner.
								if (subscriber.closed) {
									for (const track of stream.getTracks()) track.stop()
									return
								}
								subscriber.next(stream)
								subscriber.complete()
							}, error => subscriber.error(error))
						}).pipe(
							takeUntil(this.cancelVoice$),
							catchError(() => {
								this.micRefused = true
								return EMPTY
							}),
							// A recorder the browser cannot start ends this attempt, never the composer's microphone.
							switchMap(stream => this.session(stream, mode).pipe(
								finalize(() => { for (const track of stream.getTracks()) track.stop() }),
								catchError(() => {
									refuse(componentText('messageComposer.recordingFailed'))
									return EMPTY
								}),
							)),
							takeUntil(this.cancelVoice$.pipe(filter(outcome => outcome === 'discard'))),
							finalize(() => { this.voiceDone = null; resolve() }),
						)
					}),
				),
				filter((detail): detail is VoiceNoteDetail => detail !== null),
				tap(detail => {
					this.dispatchEvent(new CustomEvent('voice-note', { detail: { ...detail, refuse }, bubbles: true, composed: true }))
				}),
			),
			this.picked$.pipe(
				map(picked => {
					const accepted: File[] = []
					const refusals: FileRefusal[] = []
					for (const file of picked) {
						if (!acceptsFile(this.accept, file)) {
							refusals.push({ file, cause: 'type', message: acceptRefusal(this.accept, file) })
							continue
						}
						if (Math.max(this.heldFiles, this.files.length) + accepted.length >= this.maxFiles) {
							refusals.push({
								file,
								cause: 'tooMany',
								message:
									this.maxFiles === 1
										? componentText('messageComposer.refusal.tooMany.one')
										: componentText('messageComposer.refusal.tooMany.other', { max: this.maxFiles }),
							})
							continue
						}
						accepted.push(file)
					}
					return { accepted, refusals }
				}),
				tap(({ refusals }) => {
					this.notices = [...new Set(refusals.map(refusal => refusal.message))]
					for (const refusal of refusals) this.dispatchScopedEvent('refusal', refusal, { bubbles: true })
				}),
				filter(({ accepted }) => accepted.length > 0),
				// Batches are independent: a slow one never holds back the next.
				mergeMap(({ accepted }) => {
					const work: Promise<unknown>[] = []
					this.dispatchEvent(
						new CustomEvent('file', {
							detail: { files: accepted, refuse, until: (promise: Promise<unknown>) => work.push(promise) },
							bubbles: true,
							composed: true,
						}),
					)
					if (work.length === 0) return EMPTY
					this.adding += 1
					return from(Promise.allSettled(work)).pipe(
						tap(results => {
							for (const result of results)
								if (result.status === 'rejected')
									refuse(
										result.reason instanceof Error && result.reason.message
											? result.reason.message
											: componentText('messageComposer.refusal.failed'),
									)
						}),
						finalize(() => {
							this.adding -= 1
						}),
					)
				}),
			),
		)
			// A note being recorded is kept when the field goes, its `voice-note` raised once the recorder has stopped.
			.pipe(takeUntil(this.disconnecting.pipe(switchMap(() => this.finishVoice()))))
			.subscribe()
	}

	/** Keeps an active note before the consumer changes or closes its draft. A pending
	 *  permission request is cancelled; any late microphone grant is released. */
	finishVoice(): Promise<void> {
		const finished = this.voiceDone ?? Promise.resolve()
		this.holdStart = null
		this.cancelVoice$.next('keep')
		this.stop$.next('keep')
		return finished
	}

	/** Ends this account's capture synchronously, without handing its audio to another draft. */
	discardVoice(): Promise<void> {
		const finished = this.voiceDone ?? Promise.resolve()
		this.holdStart = null
		this.cancelVoice$.next('discard')
		return finished
	}

	private session(stream: MediaStream, mode: 'tap' | 'hold'): Observable<VoiceNoteDetail | null> {
		return defer(() => {
			const recorder = new MediaRecorder(stream)
			const chunks: Blob[] = []
			const all: number[] = []
			const context = new AudioContext()
			const analyser = context.createAnalyser()
			analyser.fftSize = 512
			context.createMediaStreamSource(stream).connect(analyser)
			const samples = new Float32Array(analyser.fftSize)
			let outcome: 'keep' | 'send' | 'discard' | 'interrupted' = 'interrupted'
			try {
				recorder.start()
			} catch (error) {
				void context.close()
				throw error
			}
			this.micRefused = false
			this.recording = mode === 'hold' && this.holdStart === null ? 'tap' : mode
			this.tenths = 0
			this.levels = []
			return merge(
				from(this.updateComplete).pipe(tap(() => this.recordingRow.value?.focus({ preventScroll: true })), ignoreElements()),
				fromEvent<BlobEvent>(recorder, 'dataavailable').pipe(tap((event: BlobEvent) => {
					if (event.data.size === 0) return
					chunks.push(event.data)
					if (this.recording !== 'paused') return
					if (this.preview) URL.revokeObjectURL(this.preview.src)
					const step = all.length / VOICE_BARS
					const bars = Array.from({ length: VOICE_BARS }, (_, bar) =>
						Math.max(0, ...all.slice(Math.floor(bar * step), Math.max(Math.floor(bar * step) + 1, Math.floor((bar + 1) * step)))),
					)
					const loudest = Math.max(...bars, 0.0001)
					this.preview = {
						src: URL.createObjectURL(new Blob(chunks, { type: recorder.mimeType })),
						seconds: this.tenths / 10,
						peaks: bars.map(bar => bar / loudest),
					}
				}), ignoreElements()),
				interval(100).pipe(filter(() => recorder.state === 'recording'), tap(() => {
					analyser.getFloatTimeDomainData(samples)
					const level = Math.min(1, Math.sqrt(samples.reduce((sum, sample) => sum + sample * sample, 0) / samples.length) * 4)
					all.push(level)
					this.tenths += 1
					this.levels = [...this.levels, level].slice(-VOICE_BARS)
				}), ignoreElements()),
				this.pause$.pipe(tap(() => {
					if (recorder.state === 'recording') {
						recorder.pause()
						this.recording = 'paused'
						recorder.requestData()
					} else if (recorder.state === 'paused') {
						if (this.preview) URL.revokeObjectURL(this.preview.src)
						this.preview = null
						recorder.resume()
						this.recording = 'locked'
					}
				}), ignoreElements()),
				// The microphone goes with the field at once; the recorder then hands over what it holds.
				this.disconnecting.pipe(tap(() => { for (const track of stream.getTracks()) track.stop() }), ignoreElements()),
				this.stop$.pipe(take(1), tap((request: 'keep' | 'send' | 'discard') => {
					outcome = request === 'send' && (this.busy || this.adding > 0 || this.disabled || this.readonly) ? 'keep' : request
					if (recorder.state !== 'inactive') recorder.stop()
				}), ignoreElements()),
				fromEvent(recorder, 'error').pipe(tap(() => {
					if (outcome !== 'discard') outcome = 'interrupted'
				}), ignoreElements()),
				fromEvent(recorder, 'stop').pipe(take(1), map(() => {
					if (outcome === 'discard') return null
					if (chunks.length === 0) {
						this.notices = [componentText('messageComposer.recordingEmpty')]
						return null
					}
					if (outcome === 'interrupted') this.notices = [componentText('messageComposer.recordingInterrupted')]
					const step = all.length / VOICE_BARS
					const bars = Array.from({ length: VOICE_BARS }, (_, bar) =>
						Math.max(0, ...all.slice(Math.floor(bar * step), Math.max(Math.floor(bar * step) + 1, Math.floor((bar + 1) * step)))),
					)
					const loudest = Math.max(...bars, 0.0001)
					return {
						audio: new Blob(chunks, { type: recorder.mimeType }),
						seconds: this.tenths / 10,
						peaks: bars.map(bar => bar / loudest),
						send: outcome === 'send',
					}
				})),
			).pipe(take(1), finalize(() => {
				if (recorder.state !== 'inactive') recorder.stop()
				void context.close()
				if (this.preview) URL.revokeObjectURL(this.preview.src)
				this.preview = null
				this.recording = 'off'
			}))
		})
	}

	override firstUpdated(changed: PropertyValues): void {
		super.firstUpdated(changed)
		if (this.autofocus)
			timer(0)
				.pipe(takeUntil(this.disconnecting))
				.subscribe(() => this.focus())
	}

	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (changed.has('commands')) this.activeCommand = 0
		if (this.commandPrefix !== null && (this.commands.length === 0 || !this.value.startsWith(this.commandPrefix))) this.commandPrefix = null
	}

	override updated(changed: PropertyValues): void {
		super.updated(changed)
		this.resize()
		if (this.commandPrefix === null && changed.get('commandPrefix') != null) this.dispatchEvent(new CustomEvent('commands-close', { bubbles: true, composed: true }))
		if (changed.has('activeCommand') || changed.has('commands')) this.shadowRoot?.getElementById(`command-${this.activeCommand}`)?.scrollIntoView({ block: 'nearest' })
		const layer = this.commandsLayer.value
		if (layer?.isConnected && !layer.matches(':popover-open')) {
			layer.showPopover()
			this.placeCommands()
		}
		this.commandsShown$.next(layer?.isConnected ?? false)
	}

	/** The overlay stands in the top layer, where nothing around the composer can clip it, so it reads where the pill is. */
	private placeCommands(): void {
		const pill = this.pill.value?.getBoundingClientRect()
		if (!pill) return
		const above = pill.top - COMMANDS_GAP_PX - COMMANDS_EDGE_MARGIN_PX
		const below = window.innerHeight - pill.bottom - COMMANDS_GAP_PX - COMMANDS_EDGE_MARGIN_PX
		const under = above < COMMANDS_ROOM_PX && below > above
		const place = {
			'--x': `${pill.left}px`,
			'--w': `${pill.width}px`,
			'--top': under ? `${pill.bottom + COMMANDS_GAP_PX}px` : 'auto',
			'--bottom': under ? 'auto' : `${window.innerHeight - pill.top + COMMANDS_GAP_PX}px`,
			'--most': `${Math.max(0, under ? below : above)}px`,
		}
		for (const [name, value] of Object.entries(place)) this.commandsLayer.value?.style.setProperty(name, value)
	}

	private shownCommands(): readonly ComposerCommand[] {
		if (this.commandPrefix === null) return []
		const query = this.value.slice(this.commandPrefix.length).trim().toLocaleLowerCase()
		return query ? this.commands.filter(command => `${command.label} ${command.secondary ?? ''}`.toLocaleLowerCase().includes(query)) : this.commands
	}

	private chooseCommand(command: ComposerCommand): void {
		this.value = ''
		this.emitChange({ value: '' })
		this.activeCommand = 0
		const kept = !this.dispatchEvent(new CustomEvent('command', { detail: { command }, bubbles: true, composed: true, cancelable: true }))
		this.commandPrefix = kept ? '' : null
	}

	/** Words beside it or not, the draft holds something it can be sent with. */
	private holdsContent(): boolean {
		return this.hasAttachments || this.hasContent || this.files.length > 0 || this.voiceNote !== null
	}

	override focus(options?: FocusOptions): void {
		this.textarea.value?.focus(options)
	}

	private resize(): void {
		const textarea = this.textarea.value
		if (!textarea) return
		textarea.style.height = 'auto'
		const style = getComputedStyle(textarea)
		const cap =
			parseFloat(style.lineHeight) * Math.max(1, this.maxRows) +
			parseFloat(style.paddingTop) +
			parseFloat(style.paddingBottom)
		textarea.style.height = `${Math.min(textarea.scrollHeight, cap)}px`
	}

	/** Files can land now: the composer takes them and nothing locks it. */
	private takesFiles(): boolean {
		return this.attach && !this.disabled && !this.readonly
	}

	private holdStarts = (event: PointerEvent): void => {
		if (event.pointerType === 'mouse' || this.disabled || this.readonly) return
		// A running recording is stopped by the next touch, not held again.
		if (this.recording !== 'off') {
			if (this.recording !== 'hold') this.pause$.next()
			return
		}
		this.holdStart = { x: event.clientX, y: event.clientY, at: event.timeStamp }
		try {
			;(event.currentTarget as Element).setPointerCapture(event.pointerId)
		} catch {
			// A pointer the platform no longer tracks cannot be captured; the hold still runs.
		}
		this.record$.next('hold')
	}

	private holdMoves = (event: PointerEvent): void => {
		if (!this.holdStart || this.recording !== 'hold') return
		if (event.clientX - this.holdStart.x <= -CANCEL_SLIDE_PX) {
			this.holdStart = null
			this.stop$.next('discard')
		} else if (event.clientY - this.holdStart.y <= -LOCK_SLIDE_PX) {
			this.holdStart = null
			this.recording = 'locked'
		}
	}

	private recordingKeys = (event: KeyboardEvent): void => {
		if (this.recording === 'off') return
		if (event.key === 'Enter') {
			if (event.composedPath().some(node => node instanceof Element && node.matches('button, input, select, a[href], [role="button"], [role="slider"], [contenteditable="true"]'))) {
				event.stopPropagation()
				return
			}
			event.preventDefault()
			event.stopPropagation()
			this.stop$.next('send')
		} else if (event.key === 'Escape') {
			event.preventDefault()
			event.stopPropagation()
			this.stop$.next('discard')
		}
	}

	send(): void {
		if (this.recording !== 'off') { this.stop$.next('send'); return }
		if (this.busy || this.adding > 0 || this.disabled || this.readonly || (!this.value.trim() && !this.holdsContent())) return
		this.notices = []
		this.commandPrefix = null
		if (this.shadowRoot?.activeElement instanceof HTMLButtonElement) this.focus({ preventScroll: true })
		this.dispatchEvent(
			new CustomEvent('send', {
				detail: { value: this.value.trim() },
				bubbles: true,
				composed: true,
				cancelable: true,
			}),
		)
	}

	/** A finger needs a larger target than a mouse, read where the row is drawn. */
	private toolSize(): 'xs' | 'sm' {
		return window.matchMedia('(pointer: coarse)').matches ? 'sm' : 'xs'
	}

	override render() {
		const offered = this.shownCommands()
		const active = offered[this.activeCommand]
		return html`
			<div
				${ref(this.pill)}
				class="relative grid rounded-[inherit] bg-surface-containerHigh focus-within:outline focus-within:outline-1 focus-within:outline-outline"
				@keydown=${this.recordingKeys}
				@dragenter=${(event: DragEvent) => {
					if (!this.takesFiles() || !event.dataTransfer?.types.includes('Files')) return
					this.dragDepth += 1
					this.dragging = true
				}}
				@dragover=${(event: DragEvent) => {
					if (this.takesFiles() && event.dataTransfer?.types.includes('Files')) event.preventDefault()
				}}
				@dragleave=${() => {
					if (!this.dragging) return
					this.dragDepth = Math.max(0, this.dragDepth - 1)
					this.dragging = this.dragDepth > 0
				}}
				@drop=${(event: DragEvent) => {
					this.dragDepth = 0
					this.dragging = false
					const dropped = Array.from(event.dataTransfer?.files ?? [])
					if (!this.takesFiles() || dropped.length === 0) return
					event.preventDefault()
					this.picked$.next(dropped)
				}}
				@paste=${(event: ClipboardEvent) => {
					const pasted = Array.from(event.clipboardData?.files ?? [])
					if (!this.takesFiles() || pasted.length === 0) return
					event.preventDefault()
					this.picked$.next(pasted)
				}}
			>
				<div
					class=${this.classMap({ 'flex min-w-0 flex-wrap items-center gap-2 px-3 pt-3': true, hidden: !this.hasAttachments && this.files.length === 0 && this.voiceNote === null })}
				>
					${when(this.files.length > 0, () => html`<schmancy-file-chips class="max-w-full" .files=${this.files}></schmancy-file-chips>`)}
					${when(
						this.voiceNote,
						note => html`<schmancy-voice-note
							class="max-w-full"
							removable
							.src=${note.src}
							.peaks=${note.peaks}
							.duration=${note.seconds}
							.transcript=${note.transcript ?? ''}
							@remove=${(event: Event) => {
								event.stopPropagation()
								this.dispatchEvent(new CustomEvent('voice-note-remove', { bubbles: true, composed: true }))
							}}
						></schmancy-voice-note>`,
					)}
					<slot
						name="attachments"
						class="contents"
						@slotchange=${(event: Event) => {
							this.hasAttachments = (event.target as HTMLSlotElement).assignedElements().length > 0
						}}
					></slot>
				</div>
				${when(this.error && this.validationMessage, () => html`<div role="alert" class="min-w-0 break-words px-3 pt-3 text-sm text-error-default">${this.validationMessage}</div>`)}
				${when(
					this.adding > 0,
					() => html`<div role="status" class="min-w-0 break-words px-3 pt-3 text-sm text-surface-onVariant">${componentText('messageComposer.adding')}</div>`,
				)}
				${when(
					this.notices.length > 0,
					() => html`<div role="alert" class="grid min-w-0 grid-cols-1 gap-0.5 break-words px-3 pt-3 text-sm text-error-default">
						${repeat(this.notices, notice => notice, notice => html`<span>${notice}</span>`)}
					</div>`,
				)}
				${when(
					this.micRefused,
					() => html`<div role="status" class="min-w-0 break-words px-3 pt-3 text-sm text-warning-default">${componentText('messageComposer.micBlocked')}</div>`,
				)}
				<div class=${this.classMap({ 'min-w-0 break-words px-3 pt-3 text-sm text-surface-onVariant': true, hidden: !this.hasNote })}>
					<slot
						name="note"
						@slotchange=${(event: Event) => {
							this.hasNote = (event.target as HTMLSlotElement).assignedNodes({ flatten: true }).length > 0
						}}
					></slot>
				</div>
				<div class="relative min-w-0">
				${when(
					this.recording !== 'off',
					() => html`<div
						${ref(this.recordingRow)} tabindex="-1"
						role="status"
						aria-label=${componentText(this.recording === 'paused' ? 'messageComposer.pausedVoice' : 'messageComposer.recording')}
						class="absolute inset-0 flex min-w-0 items-center gap-2 pl-1 pr-3"
					>
						<schmancy-icon-button
							size="sm"
							icon="delete"
							aria-label=${componentText('messageComposer.discardVoice')}
							@click=${() => this.stop$.next('discard')}
						></schmancy-icon-button>
						${when(this.recording === 'paused' && this.preview !== null,
							() => html`<schmancy-voice-note class="min-w-0 flex-1" .src=${this.preview!.src} .duration=${this.preview!.seconds} .peaks=${this.preview!.peaks}></schmancy-voice-note>`,
							() => html`						<schmancy-tone-dot color="error" class="w-2.5 h-2.5 motion-safe:animate-pulse"></schmancy-tone-dot>
						<span class="w-10 shrink-0 text-sm tabular-nums text-surface-on">${clock(this.tenths)}</span>
						<div class="flex h-6 min-w-0 flex-1 items-center gap-0.5 overflow-hidden text-error-default" aria-hidden="true">
							${repeat(
								[...Array(Math.max(0, VOICE_BARS - this.levels.length)).fill(0), ...this.levels],
								(_, i) => i,
								level =>
									html`<span class="min-w-0 flex-1 rounded-full bg-current h-(--level)" style="--level: ${Math.max(3, Math.round(level * 22))}px"></span>`,
							)}
						</div>`,
						)}
						${when(this.recording !== 'hold', () => html`<schmancy-icon-button
							size="sm" icon=${this.recording === 'paused' ? 'mic' : 'pause'}
							aria-label=${componentText(this.recording === 'paused' ? 'messageComposer.resumeVoice' : 'messageComposer.pauseVoice')}
							@click=${() => this.pause$.next()}
						></schmancy-icon-button>`)}
						<schmancy-icon-button size="sm" variant="filled" icon="send"
							aria-label=${componentText('messageComposer.send')}
							?disabled=${this.disabled || this.readonly || this.busy || this.adding > 0}
							@click=${() => this.send()}
						></schmancy-icon-button>
					</div>`,
				)}
				<div class=${this.classMap({ 'flex items-stretch min-w-0': true, invisible: this.recording !== 'off' })} ?inert=${this.recording !== 'off'} aria-hidden=${this.recording !== 'off' ? 'true' : 'false'}>
				<div class=${this.classMap({ 'min-w-0 flex-1': true, 'py-2.5': this.size === 'sm', 'py-4': this.size !== 'sm' })}>
				<textarea
					${ref(this.textarea)}
					rows="1"
					dir="auto"
					aria-label=${this.label || componentText('messageComposer.message')}
					aria-keyshortcuts=${'Enter Shift+Enter' /* schmancy-text-literal-ok: ARIA keyboard shortcut tokens are not translated. */}
					aria-invalid=${this.error ? 'true' : 'false'}
					aria-autocomplete=${ifDefined(this.commands.length > 0 ? 'list' : undefined)}
					aria-haspopup=${ifDefined(this.commands.length > 0 ? 'listbox' : undefined)}
					aria-controls=${ifDefined(offered.length > 0 ? 'commands' : undefined)}
					aria-activedescendant=${ifDefined(active ? `command-${this.activeCommand}` : undefined)}
					enterkeyhint="send"
					placeholder=${this.placeholder}
					class="block w-full min-w-0 resize-none bg-transparent px-4 py-0 text-base leading-6 text-surface-on outline-none"
					.value=${this.value}
					?disabled=${this.disabled}
					?readonly=${this.readonly}
					@input=${(event: InputEvent) => {
						if (!(event.target instanceof HTMLTextAreaElement)) return
						const wasEmpty = this.value === ''
						this.value = event.target.value
						if (wasEmpty && this.value === '/' && !event.isComposing && this.commands.length > 0 && this.commandPrefix === null) this.commandPrefix = '/'
						this.activeCommand = 0
						this.emitChange({ value: this.value })
					}}
					@blur=${() => {
						this.commandPrefix = null
						this.markTouched()
					}}
					@keydown=${(event: KeyboardEvent) => {
						if (this.recording !== 'off') return
						if (this.commandPrefix !== null && !event.isComposing && event.keyCode !== 229) {
							if (event.key === 'Escape') {
								this.commandPrefix = null
								// Answered here while something shows, so a panel around the composer doesn't close with it.
								if (offered.length === 0) return
								event.preventDefault()
								event.stopPropagation()
								return
							}
							if (offered.length > 0 && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
								event.preventDefault()
								this.activeCommand = (this.activeCommand + (event.key === 'ArrowDown' ? 1 : offered.length - 1)) % offered.length
								return
							}
							if (active && event.key === 'Enter' && !event.shiftKey) {
								event.preventDefault()
								this.chooseCommand(active)
								return
							}
						}
						if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.keyCode === 229) return
						event.preventDefault()
						this.send()
					}}
				></textarea>
				</div>
				${when(!this.hideSend, () => html`<button
					type="button"
					aria-label=${componentText('messageComposer.send')}
					aria-busy=${this.busy || this.adding > 0 ? 'true' : 'false'}
					class=${this.classMap({
						'flex shrink-0 items-center justify-center rounded-3xl bg-primary-default text-primary-on cursor-pointer hover:brightness-95 active:brightness-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-default disabled:opacity-40 disabled:cursor-default': true,
						'w-11': this.size === 'sm',
						'w-14': this.size !== 'sm',
					})}
					?disabled=${this.disabled || this.readonly || this.busy || this.adding > 0 || (!this.value.trim() && !this.holdsContent())}
					@click=${() => this.send()}
				>
					<schmancy-icon aria-hidden="true">send</schmancy-icon>
				</button>`)}
				</div>
				</div>
				<div
					class=${this.classMap({ 'relative flex min-w-0 items-center gap-0.5 px-1.5 pb-1': true, hidden: !this.hasTools && !this.voice && !this.attach })}
					@slotchange=${() => {
						this.hasTools = [...(this.shadowRoot?.querySelectorAll('slot[name="leading"], slot[name="trailing"]') ?? [])].some(
							slot => slot instanceof HTMLSlotElement && slot.assignedNodes({ flatten: true }).length > 0,
						)
					}}
				>
					${when(
						this.attach,
						() => html`<schmancy-icon-button
							class=${this.classMap({ invisible: this.recording !== 'off' })}
							?inert=${this.recording !== 'off'}
							color="neutral"
							size=${this.toolSize()}
							icon="attach_file"
							aria-label=${componentText('messageComposer.attach')}
							?disabled=${this.disabled || this.readonly}
							@click=${() => {
								// A full message answers at the paperclip rather than after a pick it would refuse.
								if (Math.max(this.heldFiles, this.files.length) >= this.maxFiles) {
									this.notices = [
										this.maxFiles === 1
											? componentText('messageComposer.refusal.tooMany.one')
											: componentText('messageComposer.refusal.tooMany.other', { max: this.maxFiles }),
									]
									return
								}
								this.picker.value?.click()
							}}
						></schmancy-icon-button>`,
					)}
					<slot name="leading" class=${this.classMap({ contents: true, invisible: this.recording !== 'off' })} ?inert=${this.recording !== 'off'}></slot>
					${when(
						this.recording === 'hold',
						() => html`<span class="absolute left-2 right-12 truncate text-sm text-surface-onVariant">${componentText('messageComposer.slideHint')}</span>`,
					)}
					<div class="ml-auto flex items-center gap-0.5">
					<slot
						name="trailing"
						class=${this.classMap({ 'flex items-center gap-0.5': true, invisible: this.recording !== 'off' })}
						?inert=${this.recording !== 'off'}
					></slot>
					${when(
						this.voice,
						() => html`<schmancy-icon-button
							class=${this.classMap({ 'touch-none': true, invisible: this.recording !== 'off' && this.recording !== 'hold' })}
							?inert=${this.recording !== 'off' && this.recording !== 'hold'}
							color="neutral"
							size=${this.toolSize()}
							variant=${this.recording === 'off' || this.recording === 'hold' ? 'text' : 'filled'}
							icon=${this.recording === 'tap' || this.recording === 'locked' ? 'stop' : 'mic'}
							aria-label=${componentText(this.recording === 'off' ? 'messageComposer.recordVoice' : 'messageComposer.keepVoice')}
							?disabled=${this.disabled || this.readonly}
							@pointerdown=${this.holdStarts}
							@pointermove=${this.holdMoves}
							@pointerup=${(event: PointerEvent) => {
								if (event.pointerType === 'mouse') return
								if (this.recording === 'off' && this.holdStart !== null && event.timeStamp - this.holdStart.at >= TOUCH_HOLD_MS) {
									void this.discardVoice()
									return
								}
								this.holdStart = null
								if (this.recording === 'hold') this.stop$.next('send')
							}}
							@pointercancel=${() => {
								if (this.recording === 'hold' || this.holdStart !== null) void this.discardVoice()
							}}
							@click=${(event: PointerEvent) => {
								// A touch press is the hold gesture; its click carries no second meaning.
								if (event.pointerType === 'touch' || event.pointerType === 'pen') return
								if (this.recording === 'off') this.record$.next('tap')
								else this.pause$.next()
							}}
						></schmancy-icon-button>`,
					)}
					</div>
				</div>
				${when(
					this.dragging,
					() => html`<div
						class="pointer-events-none absolute inset-0 grid place-items-center rounded-[inherit] bg-primary-container text-primary-onContainer outline-2 outline-dashed outline-primary-default"
					>
						<span class="text-sm font-medium">${componentText('messageComposer.dropFiles')}</span>
					</div>`,
				)}
			</div>
			${when(
				offered.length > 0,
				() => html`<div
					${ref(this.commandsLayer)}
					popover="manual"
					class="fixed top-(--top) right-auto bottom-(--bottom) left-(--x) m-0 w-(--w) max-w-none overflow-visible border-0 bg-transparent p-0"
					@mousedown=${(event: Event) => event.preventDefault()}
				>
					<schmancy-surface type="containerHigh" opacity="solid" elevated class="block rounded-2xl">
						<div
							id="commands"
							role="listbox"
							aria-label=${this.commandsLabel || this.label || componentText('messageComposer.message')}
							class="grid max-h-[min(16rem,var(--most))] gap-0.5 overflow-y-auto p-1.5"
						>
							${repeat(
								offered,
								command => command.value,
								(command, index) => html`<schmancy-option
									id=${`command-${index}`}
									.value=${command.value}
									.label=${command.label}
									.secondary=${command.secondary ?? ''}
									.icon=${command.icon ?? ''}
									.active=${index === this.activeCommand}
									@option-select=${() => this.chooseCommand(command)}
								></schmancy-option>`,
							)}
						</div>
					</schmancy-surface>
				</div>`,
			)}
			${when(
				this.commands.length > 0 && this.commandCounts,
				() => html`<div role="status" aria-live="polite" class="sr-only">
					${this.commandPrefix === null
						? ''
						: offered.length === 0
							? this.commandCounts?.none
							: offered.length === 1
								? this.commandCounts?.one
								: interpolate(this.commandCounts?.other ?? '', { count: offered.length })}
				</div>`,
			)}
			${when(
				this.attach,
				() => html`<input
					${ref(this.picker)}
					type="file"
					class="sr-only"
					tabindex="-1"
					aria-hidden="true"
					accept=${this.accept}
					?multiple=${this.maxFiles > 1}
					@change=${(event: Event) => {
						if (!(event.target instanceof HTMLInputElement)) return
						const chosen = Array.from(event.target.files ?? [])
						event.target.value = ''
						if (chosen.length > 0) this.picked$.next(chosen)
					}}
				/>`,
			)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-message-composer': SchmancyMessageComposer
	}
}
